/**
 * ExpressionAudioPlayer Component
 * Audio player for practicing expressions with recording capability
 * Features: Play/Pause button, waveform visualization, click-to-record
 */

import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Volume2, Play, Pause, Loader2, Mic, Square } from "lucide-react";
import { AudioWaveform } from "@/components/AudioWaveform";
import { cn } from "@/lib/utils";
import { PreparedExpression } from "@/types/meeting-preparation.types";
import { useDirectPCMRecorder } from "@/hooks/useDirectPCMRecorder";
import * as SpeechSDK from "microsoft-cognitiveservices-speech-sdk";
import { logger } from "@/utils/logger"

interface ExpressionAudioPlayerProps {
  expression: PreparedExpression;
  audioBlobUrl?: string;
  expressionNumber: number;
  totalExpressions: number;
  className?: string;
  onRecordingComplete?: (
    pushStream: SpeechSDK.PushAudioInputStream,
    recordingBlob?: Blob
  ) => void;
  onRecordingStart?: () => void; // Callback quando gravação inicia
}

export const ExpressionAudioPlayer = ({
  expression,
  audioBlobUrl,
  expressionNumber,
  totalExpressions,
  className,
  onRecordingComplete,
  onRecordingStart,
}: ExpressionAudioPlayerProps) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef<HTMLAudioElement>(null);

  const {
    isRecording,
    recordingTime,
    startRecording,
    stopRecording,
    error: recordingError,
  } = useDirectPCMRecorder();

  // Reset playing state when expression changes
  useEffect(() => {
    setIsPlaying(false);
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
  }, [expression.id]);

  const toggleRecording = async () => {
    if (isRecording) {
      // Stop recording and get PushStream
      const pushStream = await stopRecording();
      if (onRecordingComplete) {
        logger.log(
          "[ExpressionAudioPlayer] Recording complete, sending PushStream"
        );
        onRecordingComplete(pushStream); // No Blob, direct PushStream
      }
    } else {
      // Start recording
      if (onRecordingStart) {
        onRecordingStart();
      }
      await startRecording();
    }
  };

  const togglePlay = () => {
    if (!audioRef.current || !audioBlobUrl) return;

    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      audioRef.current.play();
      setIsPlaying(true);
    }
  };

  const handleAudioEnded = () => {
    setIsPlaying(false);
  };

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const hasAudio = !!audioBlobUrl;

  return (
    <Card className={cn("border-border/50 bg-card", className)}>
      <CardContent className="p-8">
        <div className="space-y-6">
          {/* Header with expression number */}
          <div className="space-y-3">
            <div className="text-sm font-semibold text-foreground">
              Expression {expressionNumber} of {totalExpressions}
            </div>
            <p className="text-xl font-medium text-foreground leading-relaxed">
              {expression.expression}
            </p>
          </div>

          {/* Listen Section - Centralized */}
          <div className="bg-muted/20 rounded-xl p-8 space-y-6">
            <div className="text-center">
              <p className="text-base font-medium text-foreground mb-6">
                Listen to the expression
              </p>
              <div className="flex justify-center">
                <Button
                  onClick={togglePlay}
                  disabled={!hasAudio}
                  size="lg"
                  variant="outline"
                  className={cn(
                    "w-20 h-20 rounded-full border-2 transition-all hover:scale-105",
                    isPlaying &&
                      "bg-primary text-primary-foreground border-primary"
                  )}
                >
                  {!hasAudio ? (
                    <Loader2 className="w-8 h-8 animate-spin" />
                  ) : isPlaying ? (
                    <Pause className="w-8 h-8" />
                  ) : (
                    <Play className="w-8 h-8 ml-1" />
                  )}
                </Button>
              </div>
            </div>

            {/* Divider */}
            <div className="border-t border-border" />

            {/* Record Section - Centralized */}
            <div className="text-center">
              <p className="text-base font-medium text-foreground mb-6">
                Practice your pronunciation
              </p>
              <div className="flex justify-center">
                <Button
                  onClick={() => {
                    if (!isRecording && onRecordingStart) {
                      onRecordingStart();
                    }
                    toggleRecording();
                  }}
                  size="lg"
                  variant="outline"
                  className={cn(
                    "w-20 h-20 rounded-full border-2 transition-all hover:scale-105",
                    isRecording &&
                      "bg-destructive text-destructive-foreground border-destructive animate-pulse"
                  )}
                >
                  {isRecording ? (
                    <Square className="w-8 h-8" />
                  ) : (
                    <Mic className="w-8 h-8" />
                  )}
                </Button>
              </div>
              {isRecording && (
                <div className="flex items-center justify-center gap-2 mt-3">
                  <div className="w-2 h-2 bg-destructive rounded-full animate-pulse" />
                  <span className="text-sm text-muted-foreground">
                    {formatTime(recordingTime)} / 2:00
                  </span>
                </div>
              )}
            </div>

            {/* Error message */}
            {recordingError && (
              <div className="text-sm text-destructive text-center">
                {recordingError}
              </div>
            )}
          </div>

          {/* Hidden audio element */}
          {audioBlobUrl && (
            <audio
              ref={audioRef}
              src={audioBlobUrl}
              crossOrigin="anonymous"
              preload="auto"
              controlsList="nodownload"
              onEnded={handleAudioEnded}
              onError={(e) => {
                const errorCode = e.currentTarget.error?.code;
                const errorNames: Record<number, string> = {
                  1: "ABORTED",
                  2: "NETWORK",
                  3: "DECODE",
                  4: "SRC_NOT_SUPPORTED",
                };
                const errorName = errorNames[errorCode || 0] || "UNKNOWN";
                logger.error(
                  `[ExpressionAudioPlayer] Audio error (${errorName}):`,
                  {
                    errorCode,
                    src: audioBlobUrl,
                    canPlayType: "audio/wav",
                  }
                );
              }}
              onCanPlay={() => {
                logger.log("[ExpressionAudioPlayer] Audio ready to play:", {
                  src: audioBlobUrl,
                });
              }}
            />
          )}
        </div>
      </CardContent>
    </Card>
  );
};
